import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import { Navigate } from 'react-router-dom';
import ProductDashboard from '@/components/pmt2/ProductDashboard';
import ProductHeader from '@/components/pmt2/ProductHeader';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import '../../styles/pmt3-card-styles.css';
// Import rsuite components
import { Dropdown, ButtonToolbar } from 'rsuite';
// Import minimal rsuite styles
import 'rsuite/dist/rsuite-no-reset.min.css';
// Import our custom CSS for rsuite dropdowns
import '../../styles/rsuite-dropdown-override.css';
// Import dashboard navigation CSS
import '../../styles/dashboard-navigation.css';

// Import dashboard components
import MarginDashboard from '@/components/pmt3/dashboards/MarginDashboard';
import PPCBasicsDashboard from '@/components/pmt3/dashboards/PPCBasicsDashboard';
import ASINReviewHistoryDashboard from '@/components/pmt3/dashboards/ASINReviewHistoryDashboard';
import PPCSituationHistoryDashboard from '@/components/pmt3/dashboards/PPCSituationHistoryDashboard';
import PPCStatusHistoryDashboard from '@/components/pmt3/dashboards/PPCStatusHistoryDashboard';
import PriceHistoryDashboard from '@/components/pmt3/dashboards/PriceHistoryDashboard';
import ClientNotesGlobalDashboard from '@/components/pmt3/dashboards/ClientNotesGlobalDashboard';
import ClientNotesAsinDashboard from '@/components/pmt3/dashboards/ClientNotesAsinDashboard';
import FullMarginHistoryDashboard from '@/components/pmt3/dashboards/FullMarginHistoryDashboard';
import BundleLedgerViewerDashboard from '@/components/pmt3/dashboards/BundleLedgerViewerDashboard';
import LatestGpoResultsDashboard from '@/components/pmt3/dashboards/LatestGpoResultsDashboard';
import GpoHistoryDashboard from '@/components/pmt3/dashboards/GpoHistoryDashboard';
import GpoPerformanceDashboard from '@/components/pmt3/dashboards/GpoPerformanceDashboard';
import ProblemsDashboard from '@/components/pmt3/dashboards/ProblemsDashboard';
import OutOfStocksDashboard from '@/components/pmt3/dashboards/OutOfStocksDashboard';
import FarmingSeedsDashboard from '@/components/pmt3/dashboards/FarmingSeedsDashboard';
import OverallResultsDashboard from '@/components/pmt3/dashboards/OverallResultsDashboard';
import PPCResultsDashboard from '@/components/pmt3/dashboards/PPCResultsDashboard';
import FamilySituationDashboard from '@/components/pmt3/dashboards/FamilySituationDashboard';
import LifetimeResultsDashboard from '@/components/pmt3/dashboards/LifetimeResultsDashboard';
import GlobalBidsDashboard from '@/components/pmt3/dashboards/GlobalBidsDashboard';
import InventoryDashboard from '@/components/pmt3/dashboards/InventoryDashboard';
import SeasonDashboard from '@/components/pmt3/dashboards/SeasonDashboard';
import ListingDashboard from '@/components/pmt3/dashboards/ListingDashboard';
// ** 1. Import new dashboard component **
import TargetsDashboard from '@/components/pmt3/dashboards/TargetsDashboard';
import CampaignsDashboard from '@/components/pmt3/dashboards/CampaignsDashboard';
import CampaignTrendDashboard from '@/components/pmt3/dashboards/CampaignTrendDashboard';
import CampaignStatusHistoryDashboard from '@/components/pmt3/dashboards/CampaignStatusHistoryDashboard';
import PlacementTransitionInputsDashboard from '@/components/pmt3/dashboards/PlacementTransitionInputsDashboard';
import PlacementTransitionResultsDashboard from '@/components/pmt3/dashboards/PlacementTransitionResultsDashboard';
import CampaignHistoryDashboard from '@/components/pmt3/dashboards/CampaignHistoryDashboard';
import FourWeekPPCChartsDashboard from '@/components/pmt3/dashboards/FourWeekPPCChartsDashboard';
import FourWeekRevenueProfitChartDashboard from '@/components/pmt3/dashboards/FourWeekRevenueProfitChartDashboard';


import SerpRanksDashboard from '@/components/pmt3/dashboards/SerpRanksDashboard';
import FarmingBasicsDashboard from '@/components/pmt3/dashboards/FarmingBasicsDashboard';
import ExperimentResultsDashboard from '@/components/pmt3/dashboards/ExperimentResultsDashboard';
import FarmingBidHistoryDashboard from '@/components/pmt3/dashboards/FarmingBidHistoryDashboard';
import DaypartingDashboard from '@/components/pmt3/dashboards/DaypartingDashboard';
import FarmingResultsDashboard from '@/components/pmt3/dashboards/FarmingResultsDashboard';
import AsinResultsByMonthDashboard from '@/components/pmt3/dashboards/AsinResultsByMonthDashboard';
import { YearOverYearDashboard } from '@/components/pmt3/dashboards/YearOverYearDashboard';
import { MonthOverMonthDashboard } from '@/components/pmt3/dashboards/MonthOverMonthDashboard';
import { MonthOverMonthChartsDashboard } from '@/components/pmt3/dashboards/MonthOverMonthChartsDashboard';
// Import dashboard navigation component and types
import DashboardNavigation, { DashboardType, allDashboards } from '@/components/pmt3/DashboardNavigation';

// Import context
import { usePMT3Selection } from '@/context/PMT3SelectionContext';

// LocalStorage key
const LS_DASHBOARD_KEY = 'pmt3_selectedDashboard';

// Custom dropdown component using rsuite
interface CustomDropdownProps {
  title: string;
  items: Array<{
    label: string;
    value: DashboardType;
  }>;
  onSelect: (value: DashboardType) => void;
}

const CustomDropdown = ({ title, items, onSelect }: CustomDropdownProps) => (
  <Dropdown
    title={title}
    trigger="hover"
    appearance="default"
    className="no-text-highlight"
    noCaret
  >
    {items.map((item, index) => (
      <Dropdown.Item
        key={index}
        onSelect={() => onSelect(item.value)}
      >
        {item.label}
      </Dropdown.Item>
    ))}
  </Dropdown>
);

const PMT2Page: React.FC = () => {
  const { user } = useAuth();
  const { isAdmin } = useIsAdmin();
  const { selectedCompany, selectedProduct, loading, error } = usePMT3Selection();

  // Initialize dashboard state from localStorage or default
  const [selectedDashboard, setSelectedDashboardInternal] = useState<DashboardType>(() => {
    const savedDashboard = localStorage.getItem(LS_DASHBOARD_KEY) as DashboardType | null;
    if (savedDashboard && allDashboards.some(d => d.value === savedDashboard)) {
      return savedDashboard;
    }
    return 'client-notes-global'; // Default dashboard
  });

  // Handle dashboard selection and save to localStorage
  const handleDashboardSelect = (dashboard: DashboardType) => {
    try {
        localStorage.setItem(LS_DASHBOARD_KEY, dashboard || '');
    } catch (e) {
        console.error("Failed to save dashboard to localStorage", e);
    }
    setSelectedDashboardInternal(dashboard);
  };

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" />;
  }

  // Render error state
  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  // Define dropdown items for each category
  const notesItems = [
    { label: 'Client Notes - Global', value: 'client-notes-global' as DashboardType },
    { label: 'Client Notes - ASIN', value: 'client-notes-asin' as DashboardType },
    { label: 'ASIN Review History', value: 'asin-review-history' as DashboardType },
    { label: 'Overall PPC Situation History', value: 'ppc-situation-history' as DashboardType }
  ];

  const basics1Items = [
    { label: 'PPC Basics', value: 'ppc-basics' as DashboardType },
    { label: 'PPC Status History', value: 'ppc-status-history' as DashboardType },
    { label: 'Margin', value: 'margin' as DashboardType },
    { label: 'Price History', value: 'price-history' as DashboardType },
    { label: 'Full Margin History', value: 'full-margin-history' as DashboardType }
  ];

  const basics2Items = [
    { label: 'Global Bids', value: 'global-bids' as DashboardType },
    { label: 'Problems', value: 'problems' as DashboardType },
    { label: 'Inventory', value: 'inventory' as DashboardType },
    { label: 'Out of Stocks', value: 'out-of-stocks' as DashboardType },
    { label: 'Season', value: 'season' as DashboardType },
    { label: 'Listing', value: 'listing' as DashboardType }
  ];

  const gposItems = [
    { label: 'Latest GPO Results', value: 'latest-gpo-results' as DashboardType },
    { label: 'GPO Performance', value: 'gpo-performance' as DashboardType },
    { label: 'GPO History', value: 'gpo-history' as DashboardType }
  ];

  const farmingItems = [
    // ** 2. Add Dropdown Item **
    { label: 'Targets', value: 'targets' as DashboardType },
    { label: 'Farming Basics', value: 'farming-basics' as DashboardType },
    { label: 'Farming Seeds', value: 'farming-seeds' as DashboardType },
    { label: 'Farming Results', value: 'farming-results' as DashboardType },
    { label: 'Farming Bid History', value: 'farming-bid-history' as DashboardType }
  ];

  const overallResultsItems = [
    { label: 'Overall Results', value: 'overall-results' as DashboardType },
    { label: 'PPC Results', value: 'ppc-results' as DashboardType },
    { label: 'Family Situation', value: 'family-situation' as DashboardType },
    { label: 'Lifetime Results', value: 'lifetime-results' as DashboardType }, // Already present
    { label: 'SERP Ranks', value: 'serp-ranks' as DashboardType },
    { label: 'Dayparting', value: 'dayparting' as DashboardType },
    { label: 'Latest Experiment (GP Only)', value: 'latest-experiment' as DashboardType }
  ];

  const campaignResultsItems = [
    { label: 'Campaigns', value: 'campaigns' as DashboardType },
    { label: 'Campaign History', value: 'campaign-history' as DashboardType },
    { label: '4wk Trend / Campaign', value: '4wk-trend-campaign' as DashboardType },
    { label: 'Campaign Status History', value: 'campaign-status-history' as DashboardType },
    { label: 'Placement Transition Inputs', value: 'placement-transition-inputs' as DashboardType }
  ];

  const periodResultsItems = [
    { label: 'Placement Transition Results', value: 'placement-transition-results' as DashboardType },
    { label: '4-Week PPC Charts', value: '4-week-ppc-charts' as DashboardType },
    { label: '4 Week Revenue and Profit Chart', value: '4-week-revenue-profit-chart' as DashboardType },
    { label: 'Month Over Month Charts', value: 'month-over-month-charts' as DashboardType },
    { label: 'ASIN\'s Results by Month', value: 'asin-results-by-month' as DashboardType },
    { label: 'Year Over Year Change', value: 'year-over-year-change' as DashboardType },
    { label: 'Month Over Month Change', value: 'month-over-month-change' as DashboardType }
  ];

  const ledgerViewerItems = [
    { label: 'Bundle Ledger Viewer', value: 'bundle-ledger-viewer' as DashboardType }
  ];

  // Render the selected dashboard
  const renderDashboard = () => {
    // Moved check here: Don't render if loading company/product data or if no product is selected yet
    if (loading || !selectedProduct) {
        // Provide a more informative loading/selection state if needed
        return <div className="p-6 text-center text-gray-500 dark:text-gray-400">Loading selections or select a product...</div>;
    }
    if (!selectedDashboard) return null; // Should not happen with default set

    // Now TypeScript knows selectedProduct is not null within the switch
    switch (selectedDashboard) {
      case 'margin':
        return <MarginDashboard key={`margin_${selectedProduct.id}`} product={selectedProduct} />;
      case 'ppc-basics':
        return <PPCBasicsDashboard key={`ppc_basics_${selectedProduct.id}`} product={selectedProduct} />;
      case 'asin-review-history':
        return <ASINReviewHistoryDashboard key={`asin_review_${selectedProduct.id}`} product={selectedProduct} />;
      case 'ppc-situation-history':
        return <PPCSituationHistoryDashboard key={`ppc_situation_${selectedProduct.id}`} product={selectedProduct} />;
      case 'ppc-status-history':
        return <PPCStatusHistoryDashboard key={`ppc_status_${selectedProduct.id}`} product={selectedProduct} />;
      case 'price-history':
        return <PriceHistoryDashboard key={`price_history_${selectedProduct.id}`} product={selectedProduct} />;
      case 'client-notes-global':
        return <ClientNotesGlobalDashboard key={`client_notes_global_${selectedProduct.id}`} product={selectedProduct} />;
      case 'client-notes-asin':
        return <ClientNotesAsinDashboard key={`client_notes_asin_${selectedProduct.id}`} product={selectedProduct} />;
      case 'full-margin-history':
        return <FullMarginHistoryDashboard key={`full_margin_history_${selectedProduct.id}`} product={selectedProduct} />;
      case 'bundle-ledger-viewer':
        return <BundleLedgerViewerDashboard key={`bundle_ledger_viewer_${selectedProduct.id}`} product={selectedProduct} />;
      case 'latest-gpo-results':
        return <LatestGpoResultsDashboard key={`latest_gpo_results_${selectedProduct.id}`} product={selectedProduct} />;
      case 'gpo-history':
        return <GpoHistoryDashboard key={`gpo_history_${selectedProduct.id}`} product={selectedProduct} />;
      case 'gpo-performance':
        return <GpoPerformanceDashboard key={`gpo_performance_${selectedProduct.id}`} product={selectedProduct} />;
      case 'problems':
        // Problems dashboard might only need company context, not product
        return <ProblemsDashboard key={`problems_${selectedCompany}`} />;
      case 'out-of-stocks':
         // OutOfStocks dashboard might only need product context via hook
        return <OutOfStocksDashboard key={`out_of_stocks_${selectedProduct.id}`} />;
      case 'farming-seeds':
         // FarmingSeeds dashboard might only need company context via hook
        return <FarmingSeedsDashboard key={`farming_seeds_${selectedCompany}`} />;
      case 'overall-results':
         // OverallResults dashboard uses hook internally based on selectedProduct from context
        return <OverallResultsDashboard key={`overall_results_${selectedProduct.id}`} />;
      case 'ppc-results':
         // PPCResults dashboard uses hook internally based on selectedProduct from context
        return <PPCResultsDashboard key={`ppc_results_${selectedProduct.id}`} />;
      case 'family-situation':
         // FamilySituation dashboard uses hook internally based on selectedProduct from context
        return <FamilySituationDashboard key={`family_situation_${selectedCompany}`} />;
      case 'lifetime-results':
         // LifetimeResults dashboard uses hook internally based on selectedProduct from context
        return <LifetimeResultsDashboard key={`lifetime_results_${selectedProduct.id}`} />;
      case 'global-bids':
        return <GlobalBidsDashboard key={`global_bids_${selectedProduct.id}`} product={selectedProduct} />;
      case 'inventory':
        return <InventoryDashboard key={`inventory_${selectedProduct.id}`} product={selectedProduct} />;
      case 'season':
        return <SeasonDashboard key={`season_${selectedProduct.id}`} product={selectedProduct} />;
      case 'listing':
        return <ListingDashboard key={`listing_${selectedProduct.id}`} product={selectedProduct} />;
      // ** 3. Add Switch Case **
      case 'targets':
        return <TargetsDashboard key={`targets_${selectedProduct.id}`} product={selectedProduct} />;
      case 'campaigns':
        return <CampaignsDashboard key={`campaigns_${selectedProduct.id}`} product={selectedProduct} />;
      case 'campaign-history':
        return <CampaignHistoryDashboard key={`campaign_history_${selectedProduct.id}`} product={selectedProduct} />;
      case 'farming-results':
        return <FarmingResultsDashboard key={`farming_results_${selectedProduct.id}`} />;
      // Add other cases for remaining dashboards here...
      case 'farming-basics':
        return <FarmingBasicsDashboard key={`farming_basics_${selectedProduct?.id || 'none'}`} />; // Hook handles product internally
      case 'serp-ranks':
        return <SerpRanksDashboard key={`serp_ranks_${selectedProduct?.id || 'none'}`} product={selectedProduct} />;
      case 'farming-bid-history':
        return <FarmingBidHistoryDashboard key={`farming_bid_history_${selectedProduct?.id || 'none'}`} product={selectedProduct} />;
      case 'latest-experiment':
        return <ExperimentResultsDashboard key={`latest_experiment_${selectedProduct?.id || 'none'}`} product={selectedProduct} />;
      case 'dayparting':
        return <DaypartingDashboard key={`dayparting_${selectedProduct?.id || 'none'}`} />;
      case '4wk-trend-campaign':
        return <CampaignTrendDashboard key={`4wk_trend_campaign_${selectedProduct?.id || 'none'}`} />;
      case 'campaign-status-history':
        return <CampaignStatusHistoryDashboard key={`campaign_status_history_${selectedProduct?.id || 'none'}`} />;
      case 'placement-transition-inputs':
        return <PlacementTransitionInputsDashboard key={`placement_transition_inputs_${selectedProduct?.id || 'none'}`} />;
      case 'placement-transition-results':
        return <PlacementTransitionResultsDashboard key={`placement_transition_results_${selectedProduct?.id || 'none'}`} product={selectedProduct} />;
      case '4-week-ppc-charts':
        return <FourWeekPPCChartsDashboard key={`4_week_ppc_charts_${selectedProduct?.id || 'none'}`} />;
      case '4-week-revenue-profit-chart':
        return <FourWeekRevenueProfitChartDashboard key={`4_week_revenue_profit_chart_${selectedProduct?.id || 'none'}`} />;
      case 'asin-results-by-month':
        return <AsinResultsByMonthDashboard key={`asin_results_by_month_${selectedProduct?.id || 'none'}`} />;
      case 'year-over-year-change':
        return <YearOverYearDashboard key={`year_over_year_change_${selectedProduct?.id || 'none'}`} />;
      case 'month-over-month-change':
        return <MonthOverMonthDashboard key={`month_over_month_change_${selectedProduct?.id || 'none'}`} />;
      case 'month-over-month-charts':
        return <MonthOverMonthChartsDashboard key={`month_over_month_charts_${selectedProduct?.id || 'none'}`} />;
      // case 'serp-ranks': // Example placeholder
      //   return <div>SERP Ranks Dashboard (Not Implemented)</div>;
      // case 'dayparting': // Example placeholder
      //   return <div>Dayparting Dashboard (Not Implemented)</div>;
      // case 'latest-experiment': // Example placeholder
      //   return <div>Latest Experiment Dashboard (Not Implemented)</div>;
      default:
        // Attempt to find label for default message
        const currentLabel = allDashboards.find(d => d.value === selectedDashboard)?.label || selectedDashboard;
        return (
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm">
            <h2 className="text-2xl font-bold mb-6">{currentLabel}</h2>
            <p className="text-gray-500 dark:text-gray-400">This dashboard is not yet implemented.</p>
          </div>
        );
    }
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Render header only if product is selected and not loading */}
      {selectedProduct && !loading && <ProductHeader product={selectedProduct} />}
      {/* Show loading indicator while context is loading initial data */}
      {loading && !selectedProduct && <div className="text-center p-4">Loading selections...</div>}

      {/* Dashboard Category Buttons - Centered */}
      <ButtonToolbar className="flex flex-wrap justify-center gap-2 mb-4">
        <CustomDropdown title="Notes" items={notesItems} onSelect={handleDashboardSelect} />
        <CustomDropdown title="Basics 1" items={basics1Items} onSelect={handleDashboardSelect} />
        <CustomDropdown title="Basics 2" items={basics2Items} onSelect={handleDashboardSelect} />
        <CustomDropdown title="GPOs" items={gposItems} onSelect={handleDashboardSelect} />
        <CustomDropdown title="Farming" items={farmingItems} onSelect={handleDashboardSelect} />
        <CustomDropdown title="Overall Results" items={overallResultsItems} onSelect={handleDashboardSelect} />
        <CustomDropdown title="Campaign Results" items={campaignResultsItems} onSelect={handleDashboardSelect} />
        <CustomDropdown title="Period Results" items={periodResultsItems} onSelect={handleDashboardSelect} />
        <CustomDropdown title="Ledger Viewer" items={ledgerViewerItems} onSelect={handleDashboardSelect} />
      </ButtonToolbar>

      {/* Navigation buttons - only shown when a dashboard is selected */}
      {selectedDashboard && (
        <div className="mb-2">
          <DashboardNavigation
            currentDashboard={selectedDashboard}
            onNavigate={handleDashboardSelect}
          />
        </div>
      )}

      {/* Render the selected dashboard */}
      {renderDashboard()}

    </div>
  );
};

export default PMT2Page;